import React, { useState } from "react";
import {
	Plus,
	TrendingUp,
	Calendar,
	Target,
	X,
	ChevronDown,
	ChevronUp,
	Trash2,
	Pencil,
	ArrowUpDown,
} from "lucide-react";
import CustomModal from "./CustomModal";

const PLACEHOLDER_MAP = {
	time: "5:30",
	weight: "100",
	reps: "20",
	rounds: "10",
	distance: "5.2",
	calories: "350",
};

const Records = ({
	userRecords = [],
	movements = [],
	countTypes = [],
	onAddRecord,
	onDeleteRecord,
	onEditRecord,
	loading = false,
}) => {
	const [showAddForm, setShowAddForm] = useState(false);
	const [expandedMovements, setExpandedMovements] = useState(new Set());
	const [deleteConfirm, setDeleteConfirm] = useState(null); // record id to delete
	const [editingRecord, setEditingRecord] = useState(null); // { id, movement, measurements, notes, date }
	const [sortOptions, setSortOptions] = useState({}); // { [movement]: { field, direction } }
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});

	const emptyMeasurement = { countType: "", value: "" };

	const defaultMeasurements = () => [
		{ countType: "weight", value: "" },
		{ countType: "reps", value: "" },
	];

	const [newRecord, setNewRecord] = useState({
		movement: "",
		measurements: defaultMeasurements(),
		notes: "",
		date: new Date().toISOString().split("T")[0],
	});

	const addMeasurement = () => {
		setNewRecord((prev) => ({
			...prev,
			measurements: [...prev.measurements, { countType: "", value: "" }],
		}));
	};

	const removeMeasurement = (index) => {
		setNewRecord((prev) => ({
			...prev,
			measurements: prev.measurements.filter((_, i) => i !== index),
		}));
	};

	const updateMeasurement = (index, field, val) => {
		setNewRecord((prev) => ({
			...prev,
			measurements: prev.measurements.map((m, i) =>
				i === index ? { ...m, [field]: val } : m,
			),
		}));
	};

	const handleAddRecord = async (e) => {
		e.preventDefault();
		const movement = newRecord.movement;
		const validMeasurements = newRecord.measurements.filter(
			(m) => m.countType && m.value,
		);
		const incompleteMeasurements = newRecord.measurements.filter(
			(m) => !m.countType && m.value,
		);
		if (!movement || validMeasurements.length === 0) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please select a movement and add at least one measurement.",
				type: "warning",
			});
			return;
		}
		if (incompleteMeasurements.length > 0) {
			setModal({
				show: true,
				title: "Incomplete Measurement",
				message:
					"One of your measurements has a value but no type selected. Please select a type or remove it before saving.",
				type: "warning",
			});
			return;
		}
		for (const m of validMeasurements) {
			if (!countTypes.find((ct) => ct.value === m.countType)) {
				setModal({
					show: true,
					title: "Invalid Type",
					message: "Invalid movement type selected.",
					type: "warning",
				});
				return;
			}
		}
		const measurements = validMeasurements.map((m) => {
			const info = countTypes.find((ct) => ct.value === m.countType);
			return {
				countType: m.countType,
				countTypeLabel: info.label,
				countUnit: info.unit,
				value: m.value.trim(),
			};
		});
		const primary = measurements[0];
		const record = {
			movement: movement.trim(),
			countType: primary.countType,
			countTypeLabel: primary.countTypeLabel,
			countUnit: primary.countUnit,
			value: primary.value,
			measurements,
			notes: newRecord.notes.trim(),
			date: newRecord.date,
		};
		try {
			await onAddRecord(record);
			setNewRecord({
				movement: "",
				measurements: defaultMeasurements(),
				notes: "",
				date: new Date().toISOString().split("T")[0],
			});
			setShowAddForm(false);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save movement.",
				type: "error",
			});
		}
	};

	const startEditing = (record) => {
		const measurements =
			record.measurements && record.measurements.length > 0
				? record.measurements.map((m) => ({
						countType: m.countType,
						value: m.value,
					}))
				: [{ countType: record.countType, value: record.value }];
		setEditingRecord({
			id: record.id,
			movement: record.movement,
			measurements,
			notes: record.notes || "",
			date: record.date,
		});
	};

	const updateEditMeasurement = (index, field, val) => {
		setEditingRecord((prev) => ({
			...prev,
			measurements: prev.measurements.map((m, i) =>
				i === index ? { ...m, [field]: val } : m,
			),
		}));
	};

	const addEditMeasurement = () => {
		setEditingRecord((prev) => ({
			...prev,
			measurements: [...prev.measurements, { countType: "", value: "" }],
		}));
	};

	const removeEditMeasurement = (index) => {
		setEditingRecord((prev) => ({
			...prev,
			measurements: prev.measurements.filter((_, i) => i !== index),
		}));
	};

	const handleSaveEdit = async () => {
		const validMeasurements = editingRecord.measurements.filter(
			(m) => m.countType && m.value,
		);
		const incompleteMeasurements = editingRecord.measurements.filter(
			(m) => !m.countType && m.value,
		);
		if (validMeasurements.length === 0) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please add at least one measurement with a value.",
				type: "warning",
			});
			return;
		}
		if (incompleteMeasurements.length > 0) {
			setModal({
				show: true,
				title: "Incomplete Measurement",
				message:
					"One of your measurements has a value but no type selected. Please select a type or remove it before saving.",
				type: "warning",
			});
			return;
		}
		const measurements = validMeasurements.map((m) => {
			const info = countTypes.find((ct) => ct.value === m.countType);
			return {
				countType: m.countType,
				countTypeLabel: info?.label || m.countType,
				countUnit: info?.unit || "",
				value: m.value.trim(),
			};
		});
		const primary = measurements[0];
		const updatedData = {
			countType: primary.countType,
			countTypeLabel: primary.countTypeLabel,
			countUnit: primary.countUnit,
			value: primary.value,
			measurements,
			notes: editingRecord.notes.trim(),
			date: editingRecord.date,
		};
		try {
			await onEditRecord(editingRecord.id, updatedData);
			setEditingRecord(null);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to update movement.",
				type: "error",
			});
		}
	};

	const editSelectedTypes = editingRecord
		? editingRecord.measurements.map((m) => m.countType).filter(Boolean)
		: [];

	const getMeasurements = (record) => {
		if (record.measurements && record.measurements.length > 0) {
			return record.measurements;
		}
		return [
			{
				countType: record.countType,
				countTypeLabel: record.countTypeLabel,
				countUnit: record.countUnit,
				value: record.value,
			},
		];
	};

	const formatMeasurements = (record) => {
		return getMeasurements(record)
			.map((m) => `${m.value} ${m.countUnit}`)
			.join(" × ");
	};

	const groupedRecords = userRecords.reduce((acc, record) => {
		if (!acc[record.movement]) acc[record.movement] = [];
		acc[record.movement].push(record);
		return acc;
	}, {});

	Object.keys(groupedRecords).forEach((movement) => {
		const sort = sortOptions[movement] || { field: "date", direction: "desc" };
		groupedRecords[movement].sort((a, b) => {
			if (sort.field === "date") {
				const diff = new Date(a.date) - new Date(b.date);
				return sort.direction === "desc" ? -diff : diff;
			}
			// Sort by a measurement type (weight, reps, etc.)
			const aM = getMeasurements(a).find((m) => m.countType === sort.field);
			const bM = getMeasurements(b).find((m) => m.countType === sort.field);
			const aVal = aM ? parseFloat(aM.value) || 0 : 0;
			const bVal = bM ? parseFloat(bM.value) || 0 : 0;
			const diff = aVal - bVal;
			return sort.direction === "desc" ? -diff : diff;
		});
	});

	const toggleSort = (movement, field) => {
		setSortOptions((prev) => {
			const current = prev[movement] || { field: "date", direction: "desc" };
			if (current.field === field) {
				return {
					...prev,
					[movement]: {
						field,
						direction: current.direction === "desc" ? "asc" : "desc",
					},
				};
			}
			return { ...prev, [movement]: { field, direction: "desc" } };
		});
	};

	const getSortableTypes = (records) => {
		const types = new Set();
		records.forEach((r) => {
			getMeasurements(r).forEach((m) => {
				if (m.countType) types.add(m.countType);
			});
		});
		return Array.from(types);
	};

	const toggleMovementExpanded = (movement) => {
		const newExpanded = new Set(expandedMovements);
		if (newExpanded.has(movement)) {
			newExpanded.delete(movement);
		} else {
			newExpanded.add(movement);
		}
		setExpandedMovements(newExpanded);
	};

	const getLatestRecord = (records) => {
		if (records.length === 0) return null;
		return records[0];
	};

	const selectedTypes = newRecord.measurements
		.map((m) => m.countType)
		.filter(Boolean);

	return (
		<div>
			{/* Add Record Form */}
			{showAddForm && (
				<div className="mb-4 p-3 p-md-4 bg-light rounded border movement-form-mobile">
					<h6 className="mb-3">
						<Target className="me-2" size={18} />
						Record New Movement
					</h6>
					<div className="row g-3">
						<div className="col-12 col-md-6">
							<label className="form-label fw-bold">Movement/Exercise *</label>
							<select
								className="form-select"
								value={newRecord.movement}
								onChange={(e) =>
									setNewRecord({ ...newRecord, movement: e.target.value })
								}
							>
								<option value="">Select a movement</option>
								{[...movements]
									.sort((a, b) => a.localeCompare(b))
									.map((movement) => (
										<option key={movement} value={movement}>
											{movement}
										</option>
									))}
							</select>
						</div>

						<div className="col-12 col-md-6">
							<label className="form-label fw-bold">Date *</label>
							<input
								type="date"
								className="form-control"
								value={newRecord.date}
								onChange={(e) =>
									setNewRecord({ ...newRecord, date: e.target.value })
								}
								max={new Date().toISOString().split("T")[0]}
							/>
						</div>

						{/* Measurements - stacked on mobile */}
						<div className="col-12">
							<label className="form-label fw-bold">Measurements *</label>
							{newRecord.measurements.map((measurement, index) => {
								const typeInfo = countTypes.find(
									(ct) => ct.value === measurement.countType,
								);
								const placeholder =
									PLACEHOLDER_MAP[measurement.countType] || "Enter value";

								return (
									<div key={index} className="mb-3 p-2 border rounded bg-white">
										<div className="row g-2 align-items-end">
											<div className="col-12 col-sm-5">
												<label className="form-label small mb-1">Type</label>
												<select
													className="form-select form-select-sm"
													value={measurement.countType}
													onChange={(e) =>
														updateMeasurement(
															index,
															"countType",
															e.target.value,
														)
													}
												>
													<option value="">Select type</option>
													{countTypes.map((type) => (
														<option
															key={type.value}
															value={type.value}
															disabled={
																selectedTypes.includes(type.value) &&
																type.value !== measurement.countType
															}
														>
															{type.label} ({type.unit})
														</option>
													))}
												</select>
											</div>
											<div className="col col-sm-5">
												<label className="form-label small mb-1">Value</label>
												<input
													type="text"
													className="form-control form-control-sm"
													value={measurement.value}
													onChange={(e) =>
														updateMeasurement(index, "value", e.target.value)
													}
													placeholder={placeholder}
												/>
											</div>
											{newRecord.measurements.length > 1 && (
												<div className="col-auto col-sm-2">
													<button
														type="button"
														className="btn btn-outline-danger btn-sm w-100"
														onClick={() => removeMeasurement(index)}
													>
														<Trash2 size={14} />
													</button>
												</div>
											)}
										</div>
										{typeInfo && (
											<small className="text-muted d-block mt-1">
												{typeInfo.value === "time" &&
													"Format: mm:ss or hh:mm:ss"}
												{typeInfo.value === "distance" &&
													"Example: 5.2 (for 5.2km)"}
												{typeInfo.unit && ` Unit: ${typeInfo.unit}`}
											</small>
										)}
									</div>
								);
							})}
							{newRecord.measurements.length < countTypes.length && (
								<button
									type="button"
									className="btn btn-outline-secondary btn-sm"
									onClick={addMeasurement}
								>
									<Plus size={14} className="me-1" />
									Add Another Type
								</button>
							)}
						</div>

						<div className="col-12">
							<label className="form-label fw-bold">Notes (optional)</label>
							<textarea
								className="form-control"
								rows="2"
								value={newRecord.notes}
								onChange={(e) =>
									setNewRecord({ ...newRecord, notes: e.target.value })
								}
								placeholder="Any additional notes (form, feeling, etc.)"
							/>
						</div>

						<div className="col-12 d-flex gap-2">
							<button
								type="button"
								className="btn btn-success flex-grow-1 flex-sm-grow-0"
								onClick={handleAddRecord}
								disabled={loading}
							>
								<TrendingUp size={16} className="me-1" />
								{loading ? "Saving..." : "Save"}
							</button>
							<button
								type="button"
								className="btn btn-secondary flex-grow-1 flex-sm-grow-0"
								onClick={() => setShowAddForm(false)}
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Records Display */}
			{Object.keys(groupedRecords).length === 0 ? (
				<div className="text-center py-5">
					<Target size={48} className="text-muted mb-3" />
					<p className="text-muted mb-2">No movements recorded yet!</p>
					<p className="text-muted">
						Start tracking your progress by adding your first movement.
					</p>
					<button
						className="btn btn-primary"
						onClick={() => setShowAddForm(true)}
						disabled={loading}
					>
						<Plus size={16} className="me-1" />
						Add Your First Movement
					</button>
				</div>
			) : (
				<div>
					{/* Header - stacks on mobile */}
					<div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
						<h6 className="mb-0">
							Your Movements ({Object.keys(groupedRecords).length})
						</h6>
						<button
							className="btn btn-primary btn-sm"
							onClick={() => setShowAddForm(!showAddForm)}
							disabled={loading}
						>
							{showAddForm ? (
								"Cancel"
							) : (
								<>
									<Plus size={16} className="me-1" />
									Add Movement
								</>
							)}
						</button>
					</div>

					<div className="row g-3">
						{Object.entries(groupedRecords).map(([movement, records]) => {
							const latestRecord = getLatestRecord(records);
							const isExpanded = expandedMovements.has(movement);

							return (
								<div key={movement} className="col-12">
									<div
										style={{
											borderRadius: "8px",
											overflow: "hidden",
											border: "1px solid #333",
											backgroundColor: "#1a1a1a",
										}}
									>
										{/* Movement Header */}
										<div
											className="p-3"
											onClick={() => toggleMovementExpanded(movement)}
											style={{
												cursor: "pointer",
												backgroundColor: "#1a1a1a",
												borderBottom: isExpanded ? "2px solid #d15a36" : "none",
											}}
										>
											<div className="d-flex justify-content-between align-items-start">
												<div style={{ minWidth: 0, flex: 1 }}>
													<h6
														className="mb-1 d-flex align-items-center"
														style={{ color: "#fff" }}
													>
														<TrendingUp
															size={16}
															className="me-2 flex-shrink-0"
															style={{ color: "#d15a36" }}
														/>
														<span className="text-truncate">{movement}</span>
													</h6>
													{latestRecord && (
														<div className="d-flex flex-wrap align-items-center gap-2 mt-1">
															<span
																className="badge"
																style={{
																	backgroundColor: "#d15a36",
																	fontSize: "0.75rem",
																}}
															>
																{formatMeasurements(latestRecord)}
															</span>
															<small style={{ color: "rgba(255,255,255,0.5)" }}>
																<Calendar size={12} className="me-1" />
																{new Date(
																	latestRecord.date,
																).toLocaleDateString()}
															</small>
														</div>
													)}
												</div>
												<div className="d-flex align-items-center flex-shrink-0 ms-2">
													<span
														className="badge me-1"
														style={{
															backgroundColor: "rgba(209,90,54,0.2)",
															color: "#d15a36",
															fontSize: "0.7rem",
														}}
													>
														{records.length}
													</span>
													{isExpanded ? (
														<ChevronUp size={18} style={{ color: "#d15a36" }} />
													) : (
														<ChevronDown
															size={18}
															style={{ color: "rgba(255,255,255,0.5)" }}
														/>
													)}
												</div>
											</div>
										</div>

										{/* Movement History - colourful card layout */}
										{isExpanded && (
											<div className="p-2 p-md-3">
												{/* Sort Controls */}
												{records.length > 1 && (
													<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
														<small
															style={{
																color: "rgba(255,255,255,0.5)",
																marginRight: 4,
															}}
														>
															<ArrowUpDown size={12} className="me-1" />
															Sort:
														</small>
														{[
															{ field: "date", label: "Date" },
															...getSortableTypes(records).map((type) => {
																const info = countTypes.find(
																	(ct) => ct.value === type,
																);
																return {
																	field: type,
																	label: info?.label || type,
																};
															}),
														].map((opt) => {
															const current = sortOptions[movement] || {
																field: "date",
																direction: "desc",
															};
															const isActive = current.field === opt.field;
															return (
																<button
																	key={opt.field}
																	className="btn btn-sm"
																	onClick={(e) => {
																		e.stopPropagation();
																		toggleSort(movement, opt.field);
																	}}
																	style={{
																		backgroundColor: isActive
																			? "#d15a36"
																			: "rgba(255,255,255,0.1)",
																		color: "#fff",
																		border: "none",
																		fontSize: "0.75rem",
																		padding: "0.25rem 0.6rem",
																		borderRadius: "4px",
																	}}
																>
																	{opt.label}
																	{isActive && (
																		<span style={{ marginLeft: 4 }}>
																			{current.direction === "desc" ? "↓" : "↑"}
																		</span>
																	)}
																</button>
															);
														})}
													</div>
												)}
												{records.map((record) => {
													const measurements = getMeasurements(record);
													const isEditing = editingRecord?.id === record.id;

													if (isEditing) {
														return (
															<div
																key={record.id}
																className="mb-3 p-3"
																style={{
																	borderRadius: "8px",
																	backgroundColor: "#2a2a2a",
																	border: "1px solid #d15a36",
																}}
															>
																<div className="d-flex justify-content-between align-items-center mb-2">
																	<span
																		style={{
																			color: "#fff",
																			fontWeight: 600,
																			fontSize: "0.9rem",
																		}}
																	>
																		<Pencil
																			size={14}
																			className="me-2"
																			style={{ color: "#d15a36" }}
																		/>
																		Edit Entry
																	</span>
																	<button
																		className="btn btn-sm p-0"
																		onClick={() => setEditingRecord(null)}
																		style={{
																			color: "rgba(255,255,255,0.5)",
																			lineHeight: 1,
																		}}
																	>
																		<X size={16} />
																	</button>
																</div>
																<div className="mb-2">
																	<label
																		className="form-label small mb-1"
																		style={{ color: "rgba(255,255,255,0.6)" }}
																	>
																		Date
																	</label>
																	<input
																		type="date"
																		className="form-control form-control-sm"
																		value={editingRecord.date}
																		onChange={(e) =>
																			setEditingRecord((prev) => ({
																				...prev,
																				date: e.target.value,
																			}))
																		}
																		max={new Date().toISOString().split("T")[0]}
																		style={{
																			backgroundColor: "#1a1a1a",
																			color: "#fff",
																			border: "1px solid #444",
																		}}
																	/>
																</div>
																{editingRecord.measurements.map((m, idx) => {
																	const placeholder =
																		PLACEHOLDER_MAP[m.countType] ||
																		"Enter value";
																	return (
																		<div
																			key={idx}
																			className="d-flex gap-2 mb-2 align-items-end"
																		>
																			<div style={{ flex: 1 }}>
																				<label
																					className="form-label small mb-1"
																					style={{
																						color: "rgba(255,255,255,0.6)",
																					}}
																				>
																					Type
																				</label>
																				<select
																					className="form-select form-select-sm"
																					value={m.countType}
																					onChange={(e) =>
																						updateEditMeasurement(
																							idx,
																							"countType",
																							e.target.value,
																						)
																					}
																					style={{
																						backgroundColor: "#1a1a1a",
																						color: "#fff",
																						border: "1px solid #444",
																					}}
																				>
																					<option value="">Select type</option>
																					{countTypes.map((type) => (
																						<option
																							key={type.value}
																							value={type.value}
																							disabled={
																								editSelectedTypes.includes(
																									type.value,
																								) && type.value !== m.countType
																							}
																						>
																							{type.label} ({type.unit})
																						</option>
																					))}
																				</select>
																			</div>
																			<div style={{ flex: 1 }}>
																				<label
																					className="form-label small mb-1"
																					style={{
																						color: "rgba(255,255,255,0.6)",
																					}}
																				>
																					Value
																				</label>
																				<input
																					type="text"
																					className="form-control form-control-sm"
																					value={m.value}
																					onChange={(e) =>
																						updateEditMeasurement(
																							idx,
																							"value",
																							e.target.value,
																						)
																					}
																					placeholder={placeholder}
																					style={{
																						backgroundColor: "#1a1a1a",
																						color: "#fff",
																						border: "1px solid #444",
																					}}
																				/>
																			</div>
																			{editingRecord.measurements.length >
																				1 && (
																				<button
																					type="button"
																					className="btn btn-outline-danger btn-sm"
																					onClick={() =>
																						removeEditMeasurement(idx)
																					}
																					style={{ marginBottom: "1px" }}
																				>
																					<Trash2 size={14} />
																				</button>
																			)}
																		</div>
																	);
																})}
																{editingRecord.measurements.length <
																	countTypes.length && (
																	<button
																		type="button"
																		className="btn btn-outline-secondary btn-sm mb-2"
																		onClick={addEditMeasurement}
																		style={{
																			color: "rgba(255,255,255,0.5)",
																			borderColor: "#444",
																		}}
																	>
																		<Plus size={14} className="me-1" />
																		Add Type
																	</button>
																)}
																<div className="mb-2">
																	<label
																		className="form-label small mb-1"
																		style={{ color: "rgba(255,255,255,0.6)" }}
																	>
																		Notes
																	</label>
																	<input
																		type="text"
																		className="form-control form-control-sm"
																		value={editingRecord.notes}
																		onChange={(e) =>
																			setEditingRecord((prev) => ({
																				...prev,
																				notes: e.target.value,
																			}))
																		}
																		placeholder="Optional notes"
																		style={{
																			backgroundColor: "#1a1a1a",
																			color: "#fff",
																			border: "1px solid #444",
																		}}
																	/>
																</div>
																<div className="d-flex gap-2">
																	<button
																		className="btn btn-sm flex-grow-1"
																		style={{
																			backgroundColor: "#d15a36",
																			color: "#fff",
																			border: "none",
																		}}
																		onClick={handleSaveEdit}
																		disabled={loading}
																	>
																		{loading ? "Saving..." : "Save Changes"}
																	</button>
																	<button
																		className="btn btn-sm flex-grow-1"
																		style={{
																			backgroundColor: "#444",
																			color: "#fff",
																			border: "none",
																		}}
																		onClick={() => setEditingRecord(null)}
																	>
																		Cancel
																	</button>
																</div>
															</div>
														);
													}

													return (
														<div
															key={record.id}
															className="mb-3"
															style={{
																borderRadius: "8px",
																overflow: "hidden",
															}}
														>
															{/* Date header */}
															<div
																className="d-flex justify-content-between align-items-center px-3 py-2"
																style={{
																	backgroundColor: "#1a1a1a",
																	color: "#fff",
																}}
															>
																<span
																	style={{
																		fontWeight: 600,
																		fontSize: "0.9rem",
																	}}
																>
																	<Calendar
																		size={14}
																		className="me-2"
																		style={{ opacity: 0.7 }}
																	/>
																	{new Date(record.date).toLocaleDateString(
																		"en-GB",
																		{
																			weekday: "long",
																			day: "numeric",
																			month: "long",
																			year: "numeric",
																		},
																	)}
																</span>
																<div className="d-flex gap-2">
																	<button
																		className="btn btn-sm p-0"
																		onClick={(e) => {
																			e.stopPropagation();
																			startEditing(record);
																		}}
																		disabled={loading}
																		title="Edit entry"
																		style={{ color: "#d15a36", lineHeight: 1 }}
																	>
																		<Pencil size={14} />
																	</button>
																	<button
																		className="btn btn-sm p-0"
																		onClick={(e) => {
																			e.stopPropagation();
																			setDeleteConfirm(record.id);
																		}}
																		disabled={loading}
																		title="Delete entry"
																		style={{ color: "#ff6b6b", lineHeight: 1 }}
																	>
																		<X size={16} />
																	</button>
																</div>
															</div>
															{/* Measurement type headers */}
															<div className="d-flex">
																{measurements.map((m, i) => (
																	<div
																		key={`h-${i}`}
																		className="text-center py-1"
																		style={{
																			flex: 1,
																			backgroundColor: "#d15a36",
																			color: "#fff",
																			fontSize: "0.75rem",
																			fontWeight: 700,
																			textTransform: "uppercase",
																			letterSpacing: "0.5px",
																			borderRight:
																				i < measurements.length - 1
																					? "1px solid rgba(255,255,255,0.2)"
																					: "none",
																		}}
																	>
																		{m.countTypeLabel}
																	</div>
																))}
															</div>
															{/* Measurement values */}
															<div className="d-flex">
																{measurements.map((m, i) => (
																	<div
																		key={`v-${i}`}
																		className="text-center py-2"
																		style={{
																			flex: 1,
																			backgroundColor: "#2a2a2a",
																			color: "#fff",
																			fontSize: "1.25rem",
																			fontWeight: 700,
																			borderRight:
																				i < measurements.length - 1
																					? "1px solid rgba(255,255,255,0.1)"
																					: "none",
																		}}
																	>
																		{m.value}
																		<span
																			style={{
																				fontSize: "0.7rem",
																				fontWeight: 400,
																				opacity: 0.6,
																				marginLeft: 4,
																			}}
																		>
																			{m.countUnit}
																		</span>
																	</div>
																))}
															</div>
															{/* Notes row (if any) */}
															{record.notes && (
																<div
																	className="px-3 py-1"
																	style={{
																		backgroundColor: "#222",
																		color: "rgba(255,255,255,0.6)",
																		fontSize: "0.8rem",
																		fontStyle: "italic",
																	}}
																>
																	{record.notes}
																</div>
															)}
														</div>
													);
												})}
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
			{/* Delete Confirmation Modal */}
			{deleteConfirm && (
				<div
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "rgba(0,0,0,0.5)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
						padding: "1rem",
					}}
					onClick={() => setDeleteConfirm(null)}
				>
					<div
						style={{
							backgroundColor: "#2a2a2a",
							borderRadius: "12px",
							padding: "1.5rem",
							maxWidth: "360px",
							width: "100%",
							boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
							border: "1px solid #444",
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="text-center mb-3">
							<div
								style={{
									width: 48,
									height: 48,
									borderRadius: "50%",
									backgroundColor: "rgba(209,90,54,0.15)",
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: "0.75rem",
								}}
							>
								<Trash2 size={22} color="#d15a36" />
							</div>
							<h6 style={{ marginBottom: "0.5rem", color: "#fff" }}>
								Delete Entry
							</h6>
							<p
								style={{
									fontSize: "0.9rem",
									color: "rgba(255,255,255,0.6)",
									marginBottom: 0,
								}}
							>
								Are you sure you want to delete this movement entry? This can't
								be undone.
							</p>
						</div>
						<div className="d-flex gap-2">
							<button
								className="btn flex-grow-1"
								style={{
									backgroundColor: "#444",
									color: "#fff",
									border: "none",
								}}
								onClick={() => setDeleteConfirm(null)}
							>
								Cancel
							</button>
							<button
								className="btn flex-grow-1"
								style={{
									backgroundColor: "#d15a36",
									color: "#fff",
									border: "none",
								}}
								onClick={() => {
									onDeleteRecord(deleteConfirm);
									setDeleteConfirm(null);
								}}
								disabled={loading}
							>
								{loading ? "Deleting..." : "Delete"}
							</button>
						</div>
					</div>
				</div>
			)}
			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
		</div>
	);
};

export default Records;
